import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import TextButton from 'components/Luxkit/Button/TextButton'
import PriceRowCaption from 'components/Luxkit/PricePoints/PriceRowCaption'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import PriceRowTaxesAndFees from 'components/Luxkit/PricePoints/PriceRowTaxesAndFees'
import PriceRowValueDiscount from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscount'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import Group from 'components/utils/Group'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { MobileMultiRoomMode, OfferPageActions, OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { hasIncludedGuestsExceeded } from 'lib/checkout/cartReservationUtils'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import moment from 'moment'
import React, { useCallback, useMemo } from 'react'
import { FlightsInfoByRoom, getCalendarsToBeFetchedByRoom, getFlightsInfoByRoom, isLoadingByRoom, LoadingStatusByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import MobileMultipleRoomsDurationLabel from './MobileMultipleRoomsDurationLabel'
import { EmptyArray } from 'lib/array/arrayUtils'

interface Props {
  offer: App.Offer;
  checkIn?: string;
  airports: Array<App.Airport>;
  flightOrigin?: App.Airport;
  isFlightsSelected?: boolean;
  isLoadingByRoom: LoadingStatusByRoom;
  flightsInfoByRoom: FlightsInfoByRoom;
  calendarsToBeFetchedByRoom: LoadingStatusByRoom;
  room: App.RoomOccupants;
  packageOption: OfferPackageOptionView;
  ctaDirection?: 'horizontal' | 'vertical';
  children?: React.ReactNode;
  mobileMultiRoomMode?: MobileMultiRoomMode;
  rooms: Array<App.RoomOccupants>;
}

function MobileMultipleRoomsRoomOptionPriceDetails(props: Props) {
  const {
    offer,
    checkIn,
    airports,
    flightOrigin,
    isFlightsSelected,
    isLoadingByRoom,
    flightsInfoByRoom,
    calendarsToBeFetchedByRoom,
    room,
    packageOption,
    ctaDirection = 'horizontal',
    children,
    mobileMultiRoomMode = 'individual',
    rooms,
  } = props

  const {
    package: pkg,
    flightPrice,
    hotelValue,
    hotelDiscountPercent,
    bundledDiscountPercent,
    hotelPrice,
    hotelMemberPrice,
    baseMemberPrice,
    propertyFees,
  } = packageOption

  const offerPageDispatch = useOfferPageDispatch()
  const hasFlights = !!flightPrice && !offer.flightCacheDisabled
  const showIncludedGuestsExceeded = useMemo(() => hasIncludedGuestsExceeded(room, pkg, offer) && !!pkg.roomPolicyDescription, [pkg, room, offer])
  const flightDestination = useMemo(() => airports?.find(({ code }) => code === offer.flightDestinationPort), [airports, offer.flightDestinationPort])

  const isFlightsEnabled = flightsInfoByRoom[room.roomId]?.isFlightsAvailable
  const isFetching = isLoadingByRoom[room.roomId] || calendarsToBeFetchedByRoom[room.roomId]

  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLButtonElement> | React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation()
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: e.currentTarget.dataset.filter as OfferPageFilterType })
  }, [offerPageDispatch, pkg.roomType])

  const shouldShowDiscountPercent = hotelDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const flightsBundledNoCache = !hasFlights && offer.bundledWithFlightsOnly
  const shouldShowBundledDiscountPercent = bundledDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const highestDiscountPercent = hotelDiscountPercent > bundledDiscountPercent ? hotelDiscountPercent : bundledDiscountPercent

  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const departureTooFarInFuture = useMemo(() => !!checkInMoment && checkInMoment.diff(moment.utc(), 'days') > 330, [checkInMoment])

  const totalHotelValue = useMemo(() => (
    mobileMultiRoomMode === 'same-room' ? hotelValue * rooms.length : hotelValue
  ), [hotelValue, mobileMultiRoomMode, rooms])

  const totalHotelFlightsValue = useMemo(() => (
    mobileMultiRoomMode === 'same-room' ? (hotelValue + flightPrice) * rooms.length : hotelValue + flightPrice
  ), [hotelValue, mobileMultiRoomMode, rooms, flightPrice])

  const totalPrice = useMemo(() => {
    const hotelTotal = hotelPrice + (propertyFees ?? 0)
    return mobileMultiRoomMode === 'same-room' ? hotelTotal * rooms.length : hotelTotal
  }, [hotelPrice, mobileMultiRoomMode, propertyFees, rooms])

  const totalMemberPrice = useMemo(() => {
    const hotelMemberTotal = hotelMemberPrice + (propertyFees ?? 0)

    if (hotelMemberPrice > 0) {
      return mobileMultiRoomMode === 'same-room' ? hotelMemberTotal * rooms.length : hotelMemberTotal
    }
    return 0
  }, [hotelMemberPrice, propertyFees, rooms, mobileMultiRoomMode])

  const totalFlightPrice = useMemo(() => {
    const hotelFlightTotal = hotelPrice + (propertyFees ?? 0) + flightPrice
    return mobileMultiRoomMode === 'same-room' ? hotelFlightTotal * rooms.length : hotelFlightTotal
  }, [hotelPrice, mobileMultiRoomMode, propertyFees, rooms, flightPrice])

  const totalMemberFlightPrice = useMemo(() => {
    const hotelMemberFlightTotal = hotelMemberPrice + (propertyFees ?? 0) + flightPrice

    if (hotelMemberPrice > 0) {
      return mobileMultiRoomMode === 'same-room' ? hotelMemberFlightTotal * rooms.length : hotelMemberFlightTotal
    }
    return 0
  }, [hotelMemberPrice, propertyFees, rooms, mobileMultiRoomMode, flightPrice])

  const showMemberPrice = baseMemberPrice > 0

  const shouldShowDiscount = (shouldShowDiscountPercent || shouldShowBundledDiscountPercent) && pkg.shouldDisplayValue && !offer.isDiscountPillHidden
  const shouldShowValue = hotelValue > (showMemberPrice ? hotelMemberPrice : hotelPrice) && pkg.shouldDisplayValue

  const onlyShowDiscount = shouldShowDiscount && ctaDirection === 'horizontal'
  const showValueAndDiscount = shouldShowValue && ctaDirection === 'vertical'

  return (
    <Group
      direction={ctaDirection}
      horizontalAlign={ctaDirection === 'horizontal' ? 'space-between' : 'start'}
      verticalAlign={ctaDirection === 'horizontal' ? 'end' : 'start'}
      gap={ctaDirection === 'horizontal' ? 20 : 12}
    >
      <Group direction="vertical" gap={12}>
        <LoadingIndicator visible={isFetching} floating opaque />
        {!offer.bundledWithFlightsOnly && !isFlightsSelected && (
          <Group direction="vertical" gap={8}>
            {onlyShowDiscount && <PriceRowValueDiscount
              data-testid="value-box"
              discountPercentage={highestDiscountPercent}
            />}
            <div data-testid="pricing">
              <PriceRowPriceCaption><MobileMultipleRoomsDurationLabel shouldDisplayRoom={mobileMultiRoomMode === 'same-room'} /></PriceRowPriceCaption>
              <LuxPlusPriceStack
                size="S"
                price={totalPrice}
                memberPrice={totalMemberPrice}
                saleUnit="total"
                offerConfig={offer.luxPlus}
              />
              {showValueAndDiscount && <PriceRowValueDiscountWithCaption
                data-testid="value-box"
                size="M"
                originalValue={totalHotelValue}
                discountPercentage={shouldShowDiscount ? highestDiscountPercent : undefined}
              />}
              {showIncludedGuestsExceeded && (
                <AdditionalGuestsPopup
                  complex={false}
                  description="+ Extra guest surcharge"
                  modalContent={pkg.roomPolicyDescription}
                />
              )}
            </div>
          </Group>
        )}
        {isFlightsEnabled && isFlightsSelected && (
          <Group direction="vertical" gap={12}>
            {hasFlights && (
              <Group direction="vertical" gap={8}>
                {onlyShowDiscount && <PriceRowValueDiscount
                  data-testid="value-box"
                  discountPercentage={highestDiscountPercent}
                />}
                <div data-testid="pricing">
                  <PriceRowPriceCaption><MobileMultipleRoomsDurationLabel shouldDisplayRoom={mobileMultiRoomMode === 'same-room'} /></PriceRowPriceCaption>
                  <LuxPlusPriceStack
                    size="S"
                    price={totalFlightPrice}
                    memberPrice={totalMemberFlightPrice}
                    saleUnit="total"
                    offerConfig={offer.luxPlus}
                  />
                  {showValueAndDiscount && <PriceRowValueDiscountWithCaption
                    data-testid="value-box"
                    size="M"
                    originalValue={totalHotelFlightsValue}
                    discountPercentage={shouldShowDiscount ? highestDiscountPercent : undefined}
                  />}
                  {showIncludedGuestsExceeded && (
                    <AdditionalGuestsPopup
                      complex={false}
                      description="+ Extra guest surcharge"
                      modalContent={pkg.roomPolicyDescription}
                    />
                  )}
                </div>
              </Group>
            )}

            {!hasFlights && flightOrigin?.code === flightDestination?.code && (
              <Group direction="vertical" gap={8}>
                <PriceRowCaption>
                  No flights available from {flightOrigin?.name}. Please select another departing airport.
                </PriceRowCaption>
                <TextButton
                  kind="tertiary"
                  data-filter="airport"
                  onClick={onToggleFilter}
                >
                  Change airport
                </TextButton>
              </Group>
            )}

            {!hasFlights && departureTooFarInFuture && <PriceRowCaption>
              Flights can only be booked up to 11 months in advance.
            </PriceRowCaption>}

            {!hasFlights && !flightsBundledNoCache && flightOrigin?.code !== flightDestination?.code && !departureTooFarInFuture && (
              <Group direction="vertical" gap={8}>
                {onlyShowDiscount && <PriceRowValueDiscount
                  data-testid="value-box"
                  discountPercentage={highestDiscountPercent}
                />}
                <div>
                  <PriceRowPriceCaption><MobileMultipleRoomsDurationLabel shouldDisplayRoom={mobileMultiRoomMode === 'same-room'} /></PriceRowPriceCaption>
                  <LuxPlusPriceStack
                    size="S"
                    price={totalFlightPrice}
                    saleUnit="total*"
                    memberPrice={totalMemberFlightPrice}
                    offerConfig={offer.luxPlus}
                  />
                  {showValueAndDiscount && <PriceRowValueDiscountWithCaption
                    data-testid="value-box"
                    size="M"
                    originalValue={totalHotelFlightsValue}
                    discountPercentage={shouldShowDiscount ? highestDiscountPercent : undefined}
                  />}
                  <PriceRowTaxesAndFees />
                </div>
              </Group>
            )}
            <AtolProtectedTooltip />
          </Group>
        )}
      </Group>

      {!!children && children}
    </Group>
  )
}

type MappedProps = Pick<Props,
  'offer'
  | 'checkIn'
  | 'airports'
  | 'flightOrigin'
  | 'isFlightsSelected'
  | 'isLoadingByRoom'
  | 'flightsInfoByRoom'
  | 'calendarsToBeFetchedByRoom'
  | 'mobileMultiRoomMode'
  | 'rooms'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  offer: state.offer!,
  checkIn: state.checkInDate,
  airports: appState.geo.airports,
  flightOrigin: state.airport,
  isFlightsSelected: state.flights,
  isLoadingByRoom: isLoadingByRoom(state, appState),
  flightsInfoByRoom: getFlightsInfoByRoom(state, appState),
  calendarsToBeFetchedByRoom: getCalendarsToBeFetchedByRoom(state, appState),
  mobileMultiRoomMode: state.mobileMultiRoomMode,
  rooms: state.rooms ?? EmptyArray as Array<App.RoomOccupants>,
}), MobileMultipleRoomsRoomOptionPriceDetails)
